import React, {
	Dispatch,
	memo,
	useCallback,
	useMemo,
	useState,
	useEffect,
	useRef,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Counterparty, Modal } from "../../../../services";
import { useTypedSelector } from "../../../../redux/store";
import { useTaxiServiceFilterAccessControl } from "../../../../hooks";
import MultiSelectWithModalBase, {
	MultiSelectWithModal,
} from "../MultiSelectWithModal";
import { StyledP, StyledRow, StyledColumn } from "../..";

const EqualWidthColumn = styled(StyledColumn)`
	flex: 1;
`;

const MultiSelectCounterparty: React.FC<MultiSelectCounterparty.Props> = ({
	value = [],
	onChange,
	companyIds = [],
	disabled,
	titleText,
	required,
	modalSetting,
	sort,
	subscribeOptions = {},
	canUseCompanyIds = true,
	canShowBillText = false,
	canShowCardText = false,
	taxiServiceIds,
	isOnCheckBox = true,
}) => {
	const { t } = useTranslation();
	const { accessTaxiServicesIds } = useTaxiServiceFilterAccessControl<any>();
	const lang = useTypedSelector((state) => state.session.language);

	const prevOnChange = useRef(onChange);
	useEffect(() => {
		prevOnChange.current = onChange;
	}, [onChange]);

	const [error, setError] = useState("");
	const [items, setItems] = useState<
		Modal.ModelSimpleForMultySelectModalCounterparty[]
	>([]);
	const [loading, setLoading] = useState(false);

	const texts = useMemo(
		() => ({
			title: t("contragentChoise"),
			allText: t("all_counterparty"),
			allActiveText: t("all_active_counterparty"),
			erpou: t(
				"mainPage.customers.counterparties.modal.tabs.main.mainData.edrpou",
			),
			cards: t("cards"),
			bill: t("bill"),
		}),
		[t],
	);

	const optionsService = useMemo<
		Omit<Modal.SubscribeOptions, "typedb">
	>(() => {
		const result: Omit<Modal.SubscribeOptions, "typedb"> = {
			lang,
			taxiServiceIds: taxiServiceIds || accessTaxiServicesIds,
			...subscribeOptions,
		};

		return result;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		accessTaxiServicesIds,
		lang,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		JSON.stringify(subscribeOptions),
		taxiServiceIds,
	]);

	useEffect(() => {
		if (isOnCheckBox === false) return () => {};

		let mounted = true;

		const loadData = async () => {
			setLoading(true);
			try {
				const data = await Modal.getAllCounterparty(optionsService);

				if (mounted) {
					setItems(data?.items ?? []);
				}
			} catch (err) {
				console.error("Failed to fetch Modal.getAll:", err);
				if (mounted) {
					setItems([]);
				}
			} finally {
				if (mounted) setLoading(false);
			}
		};

		loadData();

		return () => {
			mounted = false;
		};
	}, [isOnCheckBox, optionsService]);

	const selectOptions = useMemo(() => {
		const DEFAULT_TEXT_EMPTY = "Unknown";

		return items.map((item) => {
			const { id, checks } = item;
			const name = item?.name ?? DEFAULT_TEXT_EMPTY;
			const erpou = item?.edrpou ?? DEFAULT_TEXT_EMPTY;
			const erpouFull = `${texts.erpou}: ${erpou}`;

			const bill = checks
				.map((check) => check?.checkValue ?? DEFAULT_TEXT_EMPTY)
				.join(", ");
			const billFull = canShowBillText ? `; ${texts.bill}: ${bill}` : "";

			const cards = checks
				.flatMap(
					(check) =>
						check.checkCards?.map(
							(card) => card.codeValue ?? DEFAULT_TEXT_EMPTY,
						) || [],
				)
				.join(", ");
			const cardsFull = canShowCardText
				? `; ${texts.cards}: ${cards}`
				: "";

			const textRowTable = `${name} (${erpouFull}${billFull}${cardsFull})`;

			return {
				key: id,
				label: textRowTable,
				value: id,
				name: textRowTable,
				data: item,
			};
		});
	}, [
		items,
		texts.erpou,
		texts.bill,
		texts.cards,
		canShowBillText,
		canShowCardText,
	]);

	const selectedSet = useMemo(() => new Set(value), [value]);
	const selected = useMemo(
		() => selectOptions.filter((item) => selectedSet.has(item.value)),
		[selectOptions, selectedSet],
	);

	const selectOnChange = useCallback((newValue) => {
		setError("");
		prevOnChange.current(newValue.map((item) => item.value));
	}, []);

	const isDisabledThenCompanyLength = canUseCompanyIds && !companyIds.length;

	useEffect(() => {
		if (disabled || isDisabledThenCompanyLength) {
			prevOnChange.current([]);
		}
	}, [disabled, isDisabledThenCompanyLength]);

	return (
		<StyledRow gap="7px" alignItems="center">
			<EqualWidthColumn gap="7px">
				{titleText && <StyledP>{t(titleText) ?? ""}</StyledP>}
				<MultiSelectWithModal
					value={selected}
					onChange={selectOnChange}
					options={selectOptions}
					error={error}
					disabled={disabled || isDisabledThenCompanyLength}
					title={texts.title}
					allText={texts.allText}
					allActiveText={texts.allActiveText}
					required={required}
					pullUpItemInsideArray
					hiddenButton={false}
					showSelectAll
					modalSetting={modalSetting}
					sort={{ show: true, active: true, ...(sort || {}) }}
					loadingList={loading}
				/>
			</EqualWidthColumn>
		</StyledRow>
	);
};

declare namespace MultiSelectCounterparty {
	interface Props extends MultiSelectWithModalBase.Setting {
		value: Value;
		onChange: Dispatch<Value>;
		titleText?: string | string[];
		modalSetting?: MultiSelectWithModalBase.ListSelectProps["modalSetting"];
		companyIds?: number[];
		subscribeOptions?: Counterparty.SubscribeOptions;
		canUseCompanyIds?: boolean;
		canShowBillText?: boolean;
		canShowCardText?: boolean;
		taxiServiceIds?: number[];
		isOnCheckBox?: boolean;
	}

	type Value = number[];
}

export default memo(MultiSelectCounterparty);
